import { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, IconButton, Modal } from '@mui/material';
import { Close } from '@mui/icons-material';
import BraintreeWebDropIn from 'braintree-web-drop-in';
import useAnalytics from '../../hooks/useAnalytics';
import useErrorHandler from '../../hooks/useErrorHandler';
import { PAYMENT } from '../../constants/analytics';
import { PURCHASE_WIDGET } from '../../constants/strings';
import { paymentApis } from '../../apis';
import { useOverlay } from '../../features/Overlay';

const DROPIN_CONTAINER_ID = 'dropin-container';

const PurchaseWidget = ({
  handleCheckout,
  sx,
  basketId,
  getClientToken,
  amount,
  threeDSecure,
  disabled = true,
  children = PURCHASE_WIDGET.CHECKOUT,
}) => {
  const { trackAction } = useAnalytics();
  const [braintreeInstance, setBraintreeInstance] = useState();
  const [modalOpen, setModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [nonce, setNonce] = useState();
  // eslint-disable-next-line no-unused-vars
  const [liabilityShift, setLiabilityShift] = useState();
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const overlay = useOverlay();
  const { errorHandler } = useErrorHandler();

  const validateForm = fields => {
    const fieldKeys = Object.keys(fields);
    let isFormValid = true;

    // Check validity
    fieldKeys.forEach(key => {
      if (!fields[key].isValid) {
        isFormValid = false;
      }
    });

    if (isFormValid) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  };

  const subscriptions = [
    {
      'card:focus': () => {
        setErrorMessage('');
      },
    },
    {
      'card:validityChange': event => {
        validateForm(event.fields);
      },
    },
    {
      changeActiveView: event => {
        const { newViewId } = event;

        const changeActiveViewActionsMap = {
          // Disable submit button on Options view
          options: () => {
            setIsSubmitDisabled(true);
            setErrorMessage('');
          },
          paypal: () => setIsSubmitDisabled(true),

          // Enable submit button when methods are available
          methods: () => setIsSubmitDisabled(false),
          card: () => setIsSubmitDisabled(true),
        };

        // Do a view action if available
        if (changeActiveViewActionsMap[newViewId]) {
          changeActiveViewActionsMap[newViewId]();
        }
      },
    },
  ];

  const setupDropIn = async () => {
    overlay.show();
    try {
      const resolveClientToken =
        getClientToken ?? (() => paymentApis.getClientToken(basketId));
      const clientToken = await resolveClientToken();

      BraintreeWebDropIn.create(
        {
          authorization: clientToken,
          container: `#${DROPIN_CONTAINER_ID}`,
          preselectVaultedPaymentMethod: true,
          threeDSecure: true,
          paypal: {
            flow: 'checkout',
            amount,
            currency: PURCHASE_WIDGET.PAYMENT_CURRENCY_CODE,
          },
          applePay: {
            displayName: PURCHASE_WIDGET.MERCHANT_NAME,
            paymentRequest: {
              total: {
                label: PURCHASE_WIDGET.MERCHANT_NAME,
                amount,
              },
            },
          },
          googlePay: {
            googlePayVersion: 2,
            merchantId:
              import.meta.env.VITE_GOOGLE_PAY_MERCHANT_ID ||
              PURCHASE_WIDGET.MERCHANT_ID,
            transactionInfo: {
              totalPriceStatus: PURCHASE_WIDGET.TOTAL_PRICE_STATUS,
              totalPrice: amount,
              currencyCode: PURCHASE_WIDGET.PAYMENT_CURRENCY_CODE,
            },
            allowedPaymentMethods: [
              {
                type: PURCHASE_WIDGET.ALLOWED_PAYMENT_METHOD_TYPE,
                parameters: {
                  // braintree's docs recommend collecting and passing billing address information
                  // with all Google Pay transactions as a best practice.
                  billingAddressRequired: true,
                  billingAddressParameters: {
                    format: PURCHASE_WIDGET.BILLING_ADDRESS_PARAMETERS_FORMAT,
                  },
                  allowedCardNetworks: PURCHASE_WIDGET.ALLOWED_CARD_NETWORKS,
                },
              },
            ],
          },
        },
        (error, instance) => {
          overlay.hide();
          if (instance) {
            setBraintreeInstance(instance);

            // Subscriptions handling.
            // Details: https://braintree.github.io/braintree-web-drop-in/docs/current/Dropin.html#on
            subscriptions.forEach(item => {
              const eventName = Object.keys(item)[0];
              const callBack = item[eventName];
              instance.on(eventName, callBack);
            });
          }

          if (error) {
            errorHandler({
              message: PURCHASE_WIDGET.WIDGET_CREATE_ERROR,
              error,
              component: 'PurchaseWidget',
              withSentryException: true,
            });
          }
        }
      );
    } catch (error) {
      overlay.hide();
      errorHandler({
        message: PURCHASE_WIDGET.CLIENT_TOKEN_ERROR,
        error,
        component: 'PurchaseWidget',
        withSentryException: true,
      });
    }
  };

  const onCheckoutClick = event => {
    trackAction(event);
    setModalOpen(true);
    setupDropIn();
  };

  const onSubmitClick = event => {
    setErrorMessage('');
    setIsSubmitDisabled(true);
    trackAction(event);
    braintreeInstance.requestPaymentMethod(
      {
        threeDSecure: {
          amount,
          ...threeDSecure,
        },
      },
      (error, payload) => {
        if (error) {
          errorHandler({
            message: PURCHASE_WIDGET.TOKENIZATION_ERROR,
            error,
            component: 'PurchaseWidget',
            withSentryException: true,
          });
          setIsSubmitDisabled(true);

          return;
        }

        // 3D Secure liabilityShifted should be true if it is possible
        // It is not possible for PayPal balance (skip liabilityShifted check)
        if (payload.liabilityShiftPossible && !payload.liabilityShifted) {
          errorHandler({
            message: PURCHASE_WIDGET.LIABILITY_ERROR,
            error: payload,
            component: 'PurchaseWidget',
            withSentryException: true,
          });
          showNonce(payload, false);
          return;
        }
        showNonce(payload, true);
        overlay.show();
        return Promise.resolve(handleCheckout(payload))
          .catch(error => {
            errorHandler({
              message: PURCHASE_WIDGET.PAYMENT_ERROR,
              error,
              component: 'PurchaseWidget',
              withSentryException: true,
            });
          })
          .finally(() => {
            overlay.hide();
            closeModal();
          });
      }
    );
  };

  const showNonce = (payload, liabilityShift) => {
    setNonce(payload.nonce);
    setLiabilityShift(liabilityShift);
  };

  const closeModal = async () => {
    if (braintreeInstance) {
      subscriptions.forEach(item => {
        const eventName = Object.keys(item)[0];
        const callBack = item[eventName];
        braintreeInstance.off(eventName, callBack);
      });

      await braintreeInstance.teardown();
      setBraintreeInstance(null);
    }

    setModalOpen(false);
    setErrorMessage('');
    setIsSubmitDisabled(true);
  };

  return (
    <>
      <Modal
        open={modalOpen}
        aria-labelledby='purchase-modal-title'
        aria-describedby='purchase-modal-description'
      >
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'primary.contrastText',
            m: { sm: '10% auto' },
            borderRadius: 1,
            maxWidth: { sm: 600 },
            height: {
              xs: '100dvh',
              sm: 'auto',
            },
            maxHeight: {
              xs: '100dvh',
              sm: 'none',
            },
            overflowY: {
              xs: 'auto',
              sm: 'auto',
            },
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
          }}
          role='dialog'
          aria-modal='true'
        >
          <IconButton
            aria-label='close'
            sx={{ position: 'absolute', right: 5, top: 5, zIndex: 1 }}
            onClick={closeModal}
          >
            <Close />
          </IconButton>
          <Box
            id={DROPIN_CONTAINER_ID}
            sx={{ mb: 2 }}
            aria-label='Payment form'
          ></Box>
          {errorMessage ? (
            <Alert
              severity='error'
              sx={{ mb: 2 }}
              role='alert'
              aria-live='polite'
            >
              {errorMessage}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Button
              actionid={PAYMENT.CLICK_CLOSE}
              onClick={closeModal}
              variant='contained'
              color='primary'
              aria-label='Close payment modal'
            >
              {PURCHASE_WIDGET.CLOSE}
            </Button>
            <Button
              actionid={PAYMENT.CLICK_SUBMIT}
              onClick={onSubmitClick}
              variant='contained'
              color='primary'
              disabled={isSubmitDisabled}
              aria-label='Submit payment'
            >
              {PURCHASE_WIDGET.SUBMIT}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Button
        actionid={PAYMENT.CLICK_CHECKOUT}
        onClick={onCheckoutClick}
        variant='contained'
        color='primary'
        sx={sx}
        disabled={disabled}
        aria-label='Proceed to checkout'
      >
        {children}
      </Button>
    </>
  );
};

PurchaseWidget.propTypes = {
  sx: PropTypes.object,
  handleCheckout: PropTypes.func.isRequired,
  basketId: PropTypes.string,
  getClientToken: PropTypes.func,
  amount: PropTypes.string.isRequired,
  threeDSecure: PropTypes.shape({
    email: PropTypes.string,
    billingAddress: PropTypes.shape({
      givenName: PropTypes.string,
      surname: PropTypes.string,
      phoneNumber: PropTypes.string,
      streetAddress: PropTypes.string,
      extendedAddress: PropTypes.string,
      locality: PropTypes.string,
      region: PropTypes.string,
      postalCode: PropTypes.string,
      countryCodeAlpha2: PropTypes.string,
    }),
  }).isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
};

export default PurchaseWidget;
